/**
 * Own-units roster in the left column: groups first, then units by type
 * with status text, and one AUTO summary row per type for automated units.
 * Rows select, shift-click adds or removes, headings select a whole type.
 * Each type's heading sticks while its rows scroll, folds them away, and
 * carries the type's upgrades and rank-up for as many units as the stock
 * covers; a manual unit's row upgrades or ranks up just that unit.
 * Right-clicking a unit row or group heading opens `RosterMenu`; a group
 * heading shows the control slot that holds exactly its units.
 */
import { Fragment, type MouseEvent, type ReactNode, useRef, useState } from 'react';
import { unitGlyph, unitPlural, unitTypeOrder } from '@/game/rules/ui';
import { type Command, fromMilli, territoryOwners, unitMaxHp, unitRank } from '@/game/sim';
import { chevrons } from '@/game/view/activity';
import { ownGroups } from '@/game/view/groups';
import { nameOf, unitLabel } from '@/game/view/names';
import {
  type PromotionOption,
  promoteCommands,
  promoteTitle,
  promotionOptions,
  type TrainOption,
  trainCommands,
  trainOption,
  trainTitle,
  typePromotions,
  typeTrainOption,
  unitName,
} from '@/game/view/promotions';
import { costLabel } from '@/game/view/reasons';
import { stockInUnits } from '@/game/view/resources';
import type { RosterTarget } from '@/game/view/rosterMenu';
import { slotForSelection } from '@/game/view/slots';
import { tickHealings, tickSheltered } from '@/game/view/tickCache';
import { statusEnv, unitActivity, unitStatus } from '@/game/view/unitStatus';
import { unitUpgradePreview } from '@/game/view/upgradePreview';
import { VERBS, type Verb } from '@/game/view/verbs';
import { useSimFrame } from './app/simFrame';
import { Cost } from './Cost';
import { InlineRename } from './InlineRename';
import { CollapseToggle, usePersisted } from './persisted';
import { RosterMenu } from './RosterMenu';
import { UpgradeCard } from './UpgradeCard';
import { cancelRename, commitRename, startRename, type UiStore, useUi } from './ui/store';
import { VerbGlyph, VerbLabel } from './VerbLabel';
import { waitingOutside } from './waitingOutside';

interface UnitsPanelProps {
  /** The input store: the player's own names and the open rename input. */
  ui: UiStore;
  selectedUnitIds: readonly string[];
  /** Units an upgrade card is pointing at: their rows are marked. */
  highlightIds?: readonly string[];
  /** Point at units from an expanded upgrade card (the match rings them too). */
  onHighlight?: (ids: readonly string[]) => void;
  /** `additive` (shift held) adds/removes the unit instead of replacing the selection. */
  onSelectUnit: (id: string, additive: boolean) => void;
  onFocusUnit: (id: string) => void;
  /** Shift-click on an automated group's row: add the group, or remove it if all selected. */
  onSelectGroup: (ids: string[]) => void;
  /** A group row clicked (shift: add/remove it); the same row again within ~400 ms goes there. */
  onTapGroup: (number: number, additive: boolean) => void;
  /** Type header / Select army: select these units (`additive`: add or remove them). */
  onSelectMany: (ids: string[], additive: boolean) => void;
  /** Sends the upgrade and rank-up commands (tracked, so a refusal says why). */
  dispatch: (command: Command) => void;
  /** Replaces the panel title (the Units | Buildings tabs). */
  header?: ReactNode;
}

const SHELTERED_TITLE =
  "Sheltered: in your completed city's territory it can't be hit unless it attacks";

export function UnitsPanel({
  ui,
  selectedUnitIds,
  highlightIds = [],
  onHighlight,
  onSelectUnit,
  onFocusUnit,
  onSelectGroup,
  onTapGroup,
  onSelectMany,
  dispatch,
  header,
}: UnitsPanelProps) {
  // The upgrade whose card is open under its type heading ("archer>rifle-squad").
  const [openUpgrade, setOpenUpgrade] = useState<string | null>(null);
  const pointed = new Set(highlightIds);
  const pointedClass = 'bg-fill-hover outline outline-1 outline-accent';
  // Per type: index of the automated unit last focused from its summary row.
  const cycleRef = useRef<Record<string, number>>({});
  const [collapsed, setCollapsed] = usePersisted('unitsCollapsed', false);
  // Types whose rows are folded away (their heading and actions stay).
  const [foldedTypes, setFoldedTypes] = usePersisted<string[]>('unitsFoldedTypes', []);
  const frame = useSimFrame();
  const { view: state, playerId, rules, terrain } = frame;
  const names = useUi(ui, (s) => s.names);
  const slots = useUi(ui, (s) => s.slots);
  // The right-click menu: where it opened and on what.
  const [menu, setMenu] = useState<{ x: number; y: number; target: RosterTarget } | null>(null);
  function openMenu(e: MouseEvent, target: RosterTarget) {
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY, target });
  }
  const renaming = useUi(ui, (s) => s.renaming);
  const renamingHere = renaming?.site === 'row' ? renaming.id : null;
  const ownUnits = Object.values(state.units).filter((u) => u.owner === playerId);
  const army = ownUnits.filter((u) => rules.units[u.type]?.military);
  const groups = ownGroups(state, playerId);

  // Types in roster order (rules `ui.order`), only those the player has.
  const groupedTypes = unitTypeOrder(rules).filter((t) => ownUnits.some((u) => u.type === t));
  // The territory partition, once per frame: promotion checks (per type and
  // per row) and the shelter set below read it.
  const owners = territoryOwners(state, rules);
  // Per type: its upgrades and (military) rank-up for every unit of it, as
  // many as the stock covers, best units first (the panel renders once per frame).
  const typeActions = new Map<
    string,
    { promotions: PromotionOption[]; train: TrainOption | null }
  >();
  if (!collapsed) {
    for (const type of groupedTypes) {
      typeActions.set(type, {
        promotions: typePromotions(state, frame.ctx, playerId, type, frame.real, owners),
        train: rules.units[type]?.military ? typeTrainOption(state, rules, playerId, type) : null,
      });
    }
  }
  // Soldiers idle outside their city's full barracks (a move onto the city or barracks puts them in once there's room).
  const waiting = new Set([...waitingOutside(state, rules, playerId).values()].flat());
  // Units that can't be hit right now (in that territory and not attacking):
  // shelter has no map badge, so the roster says it.
  const sheltered = tickSheltered(state, { terrain, rules });
  const env = statusEnv(
    state,
    frame.ctx,
    playerId,
    frame.playerName,
    tickHealings(state, rules),
    waiting,
    names
  );
  const activity = (u: (typeof ownUnits)[number]) => unitActivity(env, u);
  const status = (u: (typeof ownUnits)[number]) => unitStatus(env, u);

  // "5 gathering, 2 farming, 1 building": most common activity first.
  function summary(units: typeof ownUnits): string {
    const counts = new Map<string, number>();
    for (const u of units) {
      // A scout's bare 'done'/'blocked' read as their full status here too.
      const raw = activity(u);
      const a = raw === 'done' || raw === 'blocked' ? status(u) : raw;
      counts.set(a, (counts.get(a) ?? 0) + 1);
    }
    return [...counts]
      .sort((a, b) => b[1] - a[1])
      .map(([a, n]) => `${n} ${a}`)
      .join(', ');
  }

  /** " · 3 sheltered" (or "" when none is). */
  function shelteredNote(units: typeof ownUnits): string {
    const n = units.filter((u) => sheltered.has(u.id)).length;
    return n > 0 ? ` · ${n} sheltered` : '';
  }

  function focusNext(type: string, units: typeof ownUnits) {
    const next = ((cycleRef.current[type] ?? -1) + 1) % units.length;
    cycleRef.current[type] = next;
    onFocusUnit(units[next].id);
  }

  /**
   * What one unit's row can do now on its own: the upgrades and rank-up it
   * could take alone (judged only while its type has some ready at all: with
   * none ready for the type, none is for any one unit of it).
   */
  function unitActions(u: (typeof ownUnits)[number]) {
    const forType = typeActions.get(u.type);
    const promotions = forType?.promotions.some((o) => o.ready.length > 0)
      ? promotionOptions(state, frame.ctx, playerId, [u], frame.real, owners).filter(
          (o) => o.ready.length > 0
        )
      : [];
    const one = forType?.train?.ready.length ? trainOption(state, rules, playerId, [u]) : null;
    return { promotions, train: one && one.ready.length > 0 ? one : null };
  }

  function sendAll(commands: Command[]) {
    for (const command of commands) dispatch(command);
  }

  function hpLabel(u: (typeof ownUnits)[number]): string {
    return `${Math.ceil(fromMilli(u.hp))}/${Math.ceil(fromMilli(unitMaxHp(rules, u)))}`;
  }

  return (
    <div className="panel pointer-events-auto flex max-h-full min-h-0 w-full flex-col p-3 text-xs">
      <div className="flex shrink-0 items-center justify-between gap-2">
        {header ?? (
          <p className="panel-title px-1">
            Units <span className="text-fg-subtle">{ownUnits.length}</span>
          </p>
        )}
        {army.length > 0 && (
          <button
            type="button"
            title="Select all your fighters; shift-click adds them to the selection"
            aria-label={header ? 'Select army' : undefined}
            onClick={(e) =>
              onSelectMany(
                army.map((u) => u.id),
                e.shiftKey
              )
            }
            className="btn btn-secondary ml-auto h-6 px-2 text-[11px]"
          >
            {header ? <>⚔ {army.length}</> : <>Select army {army.length}</>}
          </button>
        )}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          label="units"
        />
      </div>
      {!collapsed && (
        <div className="min-h-0 overflow-y-auto">
          {groups.map((g) => {
            const hp = fromMilli(g.units.reduce((sum, u) => sum + u.hp, 0));
            const maxHp = Math.ceil(
              fromMilli(g.units.reduce((sum, u) => sum + unitMaxHp(rules, u), 0))
            );
            const selected = g.units.every((u) => selectedUnitIds.includes(u.id));
            const topRank = Math.max(0, ...g.units.map((u) => unitRank(rules, u)));
            const groupName = nameOf(names, g.id);
            const slotNumber = slotForSelection(
              state,
              playerId,
              slots,
              g.units.map((u) => u.id)
            );
            const rowClass = `mt-1 flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-left transition ${selected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`;
            if (renamingHere === g.id) {
              return (
                <div key={g.id} className={rowClass}>
                  <InlineRename
                    value={groupName ?? ''}
                    label={`Rename Group ${g.number}`}
                    onCommit={(name) => commitRename(ui, g.id, name)}
                    onCancel={() => cancelRename(ui)}
                    className="flex-1"
                  />
                </div>
              );
            }
            return (
              <button
                key={g.id}
                type="button"
                title={`${summary(g.units)}. Click selects the group (key ${g.number}); click again goes there; shift-click adds it; double-click or F2 renames it; right-click for more`}
                onClick={(e) => {
                  // A double-click renames; it must not also count as a second tap (pan).
                  if (e.detail > 1) return;
                  onTapGroup(g.number, e.shiftKey);
                }}
                onDoubleClick={() => startRename(ui, g.id, 'row')}
                onContextMenu={(e) =>
                  openMenu(e, { kind: 'group', id: g.id, unitIds: g.units.map((u) => u.id) })
                }
                className={rowClass}
              >
                {slotNumber !== null && (
                  <span
                    title={`Control slot ${slotNumber} holds this group`}
                    className="chip px-1.5 text-[10px] tabular-nums text-accent"
                  >
                    {slotNumber}
                  </span>
                )}
                {groupName ? (
                  <span className="min-w-0 max-w-[8rem] truncate font-medium">{groupName}</span>
                ) : (
                  <span className="font-medium text-fg-muted">Group {g.number}</span>
                )}
                {topRank > 0 && (
                  <span
                    title={`Highest rank ${topRank}`}
                    className="text-[8px] leading-none text-warn"
                  >
                    {chevrons(topRank)}
                  </span>
                )}
                <span className="flex-1 truncate text-fg-subtle">
                  {g.units.length} units · {Math.ceil(hp)}/{maxHp} hp
                  {shelteredNote(g.units)}
                </span>
              </button>
            );
          })}
          {ownUnits.length === 0 ? (
            <p className="px-1 text-fg-muted">No units</p>
          ) : (
            groupedTypes.map((type) => {
              const units = ownUnits.filter((u) => u.type === type);
              const autoUnits = units.filter((u) => u.auto);
              // Grouped units are listed under their group.
              const manualUnits = units.filter((u) => !u.auto && u.groupId === undefined);
              const hurt = autoUnits.filter((u) => u.hp < unitMaxHp(rules, u)).length;
              const groupSelected =
                autoUnits.length > 0 && autoUnits.every((u) => selectedUnitIds.includes(u.id));
              const folded = foldedTypes.includes(type);
              const actions = typeActions.get(type);
              const waitingHere = units.filter((u) => waiting.has(u.id));
              return (
                <div key={type} className="mt-2">
                  {/* Sticks to the top while this type's rows scroll under it. */}
                  <div className="sticky-heading pb-1">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        title={`Select all ${unitPlural(rules, type).toLowerCase()}; shift-click adds them to the selection`}
                        onClick={(e) =>
                          onSelectMany(
                            units.map((u) => u.id),
                            e.shiftKey
                          )
                        }
                        className="min-w-0 flex-1 truncate rounded-md px-1 py-0.5 text-left text-fg-muted transition hover:bg-fill hover:text-fg"
                      >
                        {/* "Workers (26)": reads apart from the city panel's "Worker" Train card. */}
                        {unitPlural(rules, type)}{' '}
                        <span className="text-fg-subtle">({units.length})</span>
                      </button>
                      {waitingHere.length > 0 && (
                        <button
                          type="button"
                          title="Waiting outside a full barracks: once a slot frees, move them onto the city or barracks to go in. Click to select them"
                          onClick={(e) =>
                            onSelectMany(
                              waitingHere.map((u) => u.id),
                              e.shiftKey
                            )
                          }
                          className="chip shrink-0 px-1.5 text-[10px] text-warn transition hover:bg-fill-hover"
                        >
                          {waitingHere.length} outside
                        </button>
                      )}
                      <CollapseToggle
                        collapsed={folded}
                        onToggle={() =>
                          setFoldedTypes(
                            folded ? foldedTypes.filter((t) => t !== type) : [...foldedTypes, type]
                          )
                        }
                        label={unitPlural(rules, type).toLowerCase()}
                      />
                    </div>
                    {actions?.promotions.map((option) => {
                      const open = openUpgrade === `${type}>${option.to}`;
                      return (
                        <div key={option.to} className="flex items-center gap-1">
                          <div className="min-w-0 flex-1">
                            <TypeAction
                              verb="upgrade"
                              label={unitName(rules, option.to, Math.max(1, option.ready.length))}
                              option={option}
                              title={`${promoteTitle(rules, option)}. Best units first: those where they can be upgraded, then the highest rank and most hp`}
                              onClick={() => sendAll(promoteCommands(option))}
                            />
                          </div>
                          <button
                            type="button"
                            aria-expanded={open}
                            aria-label={`What upgrading to ${unitName(rules, option.to, 1)} changes`}
                            title="What it changes, needs and affects"
                            onClick={() => setOpenUpgrade(open ? null : `${type}>${option.to}`)}
                            className={`btn btn-ghost btn-icon mt-0.5 h-6 w-6 shrink-0 text-[11px] ${open ? 'bg-fill-hover' : ''}`}
                          >
                            ⓘ
                          </button>
                        </div>
                      );
                    })}
                    {actions?.train && (
                      <TypeAction
                        verb="rankUp"
                        option={actions.train}
                        title={`${trainTitle(rules, actions.train)}. One rank each, best units first`}
                        onClick={() => actions.train && sendAll(trainCommands(actions.train))}
                      />
                    )}
                  </div>
                  {actions?.promotions.map((option) =>
                    openUpgrade === `${type}>${option.to}` ? (
                      <Fragment key={option.to}>
                        <UpgradeCard
                          preview={unitUpgradePreview(
                            state,
                            frame.ctx,
                            playerId,
                            type,
                            option,
                            frame.real,
                            frame.cityNumbers,
                            names
                          )}
                          stock={stockInUnits(state, playerId)}
                          onConfirm={() => sendAll(promoteCommands(option))}
                          onHighlight={onHighlight}
                        />
                      </Fragment>
                    ) : null
                  )}
                  <div className={`space-y-0.5 ${folded ? 'hidden' : ''}`}>
                    {autoUnits.length > 0 ? (
                      <button
                        type="button"
                        title="Click to focus the next one; shift-click to add or remove them all from the selection"
                        onClick={(e) => {
                          if (e.shiftKey) onSelectGroup(autoUnits.map((u) => u.id));
                          else focusNext(type, autoUnits);
                        }}
                        className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-left transition ${autoUnits.some((u) => pointed.has(u.id)) ? pointedClass : groupSelected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
                      >
                        <span className="w-5 flex-shrink-0 font-medium">
                          {unitGlyph(rules, type)}
                        </span>
                        <span className="tabular-nums text-fg-muted">{autoUnits.length}</span>
                        <span className="flex-1 truncate text-[11px] text-fg-subtle">
                          {summary(autoUnits)}
                          {shelteredNote(autoUnits)}
                        </span>
                        {hurt > 0 ? (
                          <span className="text-[11px] tabular-nums text-warn">{hurt} hurt</span>
                        ) : null}
                        <span className="chip px-1.5 text-[10px] font-medium uppercase tracking-wide text-ok">
                          auto
                        </span>
                      </button>
                    ) : null}
                    {manualUnits.map((u) => {
                      const isSelected = selectedUnitIds.includes(u.id);
                      const own = unitActions(u);
                      return (
                        // biome-ignore lint/a11y/useSemanticElements: spec mandates div here so a <button> can nest inside
                        <div
                          key={u.id}
                          role="button"
                          tabIndex={0}
                          aria-label={`Select ${unitLabel(names, u.id, rules.units[u.type]?.name ?? u.type)} ${u.id}`}
                          title="Double-click or F2 to rename"
                          onClick={(e) => onSelectUnit(u.id, e.shiftKey)}
                          onDoubleClick={() => startRename(ui, u.id, 'row')}
                          onContextMenu={(e) =>
                            openMenu(e, { kind: 'unit', id: u.id, unitIds: [u.id] })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onSelectUnit(u.id, e.shiftKey);
                            }
                          }}
                          className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-left transition ${pointed.has(u.id) ? pointedClass : isSelected ? 'bg-fill-hover ring-1 ring-accent/70' : 'hover:bg-fill'}`}
                        >
                          <span className="w-5 flex-shrink-0 font-medium">
                            {unitGlyph(rules, u.type)}
                          </span>
                          {unitRank(rules, u) > 0 && (
                            <span
                              title={`Rank ${unitRank(rules, u)}`}
                              className="text-[8px] leading-none text-warn"
                            >
                              {chevrons(unitRank(rules, u))}
                            </span>
                          )}
                          {renamingHere === u.id ? (
                            <InlineRename
                              value={nameOf(names, u.id) ?? ''}
                              label={`Rename ${rules.units[u.type]?.name ?? u.type}`}
                              onCommit={(name) => commitRename(ui, u.id, name)}
                              onCancel={() => cancelRename(ui)}
                              className="w-24"
                            />
                          ) : (
                            nameOf(names, u.id) && (
                              <span className="min-w-0 max-w-[7rem] truncate">
                                <span className="font-medium">{nameOf(names, u.id)}</span>
                                <span className="text-fg-subtle">
                                  {' '}
                                  · {rules.units[u.type]?.name ?? u.type}
                                </span>
                              </span>
                            )
                          )}
                          <span className="tabular-nums text-fg-muted">{hpLabel(u)}</span>
                          <span className="flex-1 truncate text-[11px] text-fg-subtle">
                            {status(u)}
                          </span>
                          {sheltered.has(u.id) && (
                            <span title={SHELTERED_TITLE} className="text-[10px] text-ok">
                              sheltered
                            </span>
                          )}
                          {own.promotions.map((option) => (
                            <button
                              key={option.to}
                              type="button"
                              aria-label={`${VERBS.upgrade.label} to ${unitName(rules, option.to, 1)}`}
                              title={`${VERBS.upgrade.label} just this one to ${unitName(rules, option.to, 1)} (${costLabel(option.cost)})`}
                              onClick={(e) => {
                                e.stopPropagation();
                                sendAll(promoteCommands(option));
                              }}
                              className="btn btn-ghost btn-icon h-6 w-5 shrink-0 text-[11px] text-accent"
                            >
                              <VerbGlyph verb="upgrade" />
                            </button>
                          ))}
                          {own.train && (
                            <button
                              type="button"
                              aria-label={VERBS.rankUp.label}
                              title={`${VERBS.rankUp.label} just this one to rank ${unitRank(rules, u) + 1} (${costLabel(own.train.cost)})`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (own.train) sendAll(trainCommands(own.train));
                              }}
                              className="btn btn-ghost btn-icon h-6 w-5 shrink-0 text-[11px] text-warn"
                            >
                              <VerbGlyph verb="rankUp" />
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label="Focus camera on unit"
                            title="Focus"
                            onClick={(e) => {
                              e.stopPropagation();
                              onFocusUnit(u.id);
                            }}
                            className="btn btn-ghost btn-icon h-6 w-6 text-xs"
                          >
                            ⌖
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
      {menu && (
        <RosterMenu
          {...menu}
          title={
            nameOf(names, menu.target.id) ??
            (menu.target.kind === 'group'
              ? `Group ${groups.find((g) => g.id === menu.target.id)?.number ?? ''}`
              : unitName(rules, state.units[menu.target.id]?.type ?? '', 1))
          }
          state={state}
          playerId={playerId}
          ui={ui}
          onFocusUnit={onFocusUnit}
          onSelectMany={onSelectMany}
          dispatch={dispatch}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

/**
 * A type heading's action: "⬆ Rifle Squads ×4 · cost" (upgrade as many of
 * the type as the stock and cap allow) or "★ Rank up ×3 · cost". With none
 * ready it's disabled and says why instead of the cost.
 */
function TypeAction({
  verb,
  label,
  option,
  title,
  onClick,
}: {
  verb: Verb;
  label?: string;
  option: { ready: readonly unknown[]; cost: Record<string, number>; reason: string | null };
  title: string;
  onClick: () => void;
}) {
  const n = option.ready.length;
  return (
    <button
      type="button"
      disabled={n === 0}
      title={title}
      onClick={onClick}
      className="btn btn-secondary mt-0.5 h-6 w-full justify-between gap-1.5 px-2 text-[11px] font-normal disabled:opacity-70"
    >
      <span className="shrink-0 whitespace-nowrap">
        <VerbLabel verb={verb}>{label}</VerbLabel>
        {n > 0 && <span className="tabular-nums text-fg-muted"> ×{n}</span>}
      </span>
      <span className={`min-w-0 truncate tabular-nums ${n > 0 ? 'text-fg-muted' : 'text-warn'}`}>
        {n > 0 ? <Cost cost={option.cost} /> : (option.reason ?? 'Not now')}
      </span>
    </button>
  );
}
